'use client';

import * as React from 'react';
import type { CSSProperties, TextareaHTMLAttributes } from 'react';
import { cx } from '../utils';
import { IS_PROD, devWarnValueUndefined } from '../internal/devWarn';

/* Controlled-only contract (same rationale as Input): Textarea
   coerces `value ?? ''` internally, so `defaultValue` is ignored.
   Pass `value` + `onChange`. */
export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'style'
> {
  className?: string;
  style?: CSSProperties;
  minRows?: number;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { value, onChange, placeholder, disabled, rows = 3, minRows, className, style, ...rest },
  ref,
) {
  /* Mount-only dev-warn; see Input.tsx for the rationale. */
  React.useEffect(() => {
    if (!IS_PROD && value === undefined) devWarnValueUndefined('Textarea');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* Height floor for minRows uses --lh-caption (1.5), the kit's
     "comfortable body" leading, to match the visual rhythm of other
     stacked fields. Swap to any lh.* token for a denser baseline. */
  const areaStyle: CSSProperties | undefined = minRows
    ? { minHeight: `calc(${minRows} * var(--lh-caption) * 1em)` }
    : undefined;

  return (
    <div
      className={cx('j-field', className)}
      data-disabled={disabled ? 'true' : undefined}
      style={{ alignItems: 'stretch', ...style }}
    >
      <textarea
        {...rest}
        ref={ref}
        className="j-textarea"
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        style={areaStyle}
      />
    </div>
  );
});
